import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { compressImage } from '../lib/utils';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Image, X, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SellProductPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [categories, setCategories] = useState([]);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [category, setCategory] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [shippingInfo, setShippingInfo] = useState('');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 5) {
      toast.error('Maximum 5 photos');
      return;
    }

    for (const file of files) {
      if (file.type.startsWith('image/')) {
        try {
          const compressed = await compressImage(file, 800, 0.8);
          setImages(prev => [...prev, compressed]);
        } catch (error) {
          toast.error('Error processing photo');
        }
      }
    }
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !price || !category) {
      toast.error('Ranpli tout chan obligatwa yo');
      return;
    }

    if (parseFloat(price) < 0.99) {
      toast.error('Pri minimum se $0.99');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/marketplace/products`, {
        title,
        description,
        price: parseFloat(price),
        category,
        quantity: parseInt(quantity),
        shipping_info: shippingInfo,
        images
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Pwodui ajoute!');
      navigate('/marketplace');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto pb-20" data-testid="sell-product-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="font-bold text-lg">Vann yon Pwodui</h1>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="p-4 space-y-6">
        {/* Images */}
        <div className="space-y-2">
          <Label>Foto yo (maks 5)</Label>
          <div className="flex gap-2 flex-wrap">
            {images.map((img, index) => (
              <div key={index} className="relative w-20 h-20 rounded-xl overflow-hidden">
                <img src={img} alt={`Product ${index + 1}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            {images.length < 5 && (
              <>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-20 h-20 rounded-xl border-2 border-dashed border-border flex items-center justify-center hover:border-primary transition-colors"
                  data-testid="add-image-btn"
                >
                  <Plus className="w-6 h-6 text-muted-foreground" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="title">Tit *</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Non pwodui a"
            className="rounded-2xl"
            required
            data-testid="product-title"
          />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Dekri pwodui ou a..."
            className="rounded-2xl resize-none"
            rows={4}
            data-testid="product-description"
          />
        </div>

        {/* Price */}
        <div className="space-y-2">
          <Label htmlFor="price">Pri ($) *</Label>
          <Input
            id="price"
            type="number"
            step="0.01"
            min="0.99"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="0.00"
            className="rounded-2xl"
            required
            data-testid="product-price"
          />
          <p className="text-xs text-muted-foreground">
            Ou pral resevwa 60% (${ price ? (parseFloat(price) * 0.6).toFixed(2) : '0.00' }) apre vant lan
          </p>
        </div>

        {/* Category */}
        <div className="space-y-2">
          <Label>Kategori *</Label>
          <Select value={category} onValueChange={setCategory} required>
            <SelectTrigger className="rounded-2xl" data-testid="product-category">
              <SelectValue placeholder="Chwazi yon kategori" />
            </SelectTrigger>
            <SelectContent>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Quantity */}
        <div className="space-y-2">
          <Label htmlFor="quantity">Kantite disponib</Label>
          <Input
            id="quantity"
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="rounded-2xl"
            data-testid="product-quantity"
          />
        </div>

        {/* Shipping Info */}
        <div className="space-y-2">
          <Label htmlFor="shipping">Shipping information</Label>
          <Textarea
            id="shipping"
            value={shippingInfo}
            onChange={(e) => setShippingInfo(e.target.value)}
            placeholder="Ki jan ou pral livre pwodui a..."
            className="rounded-2xl resize-none"
            rows={2}
            data-testid="product-shipping"
          />
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={loading}
          className="w-full rounded-full h-12 font-bold"
          data-testid="submit-product"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            'Pibliye Pwodui'
          )}
        </Button>
      </form>
    </div>
  );
}
